import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { fetchAdmin, jsonHeaders } from "../../utils/adminApi";
import { Plus, Save, X, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function UsersPanel({ users, programs, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "trekker", program_id: "" });
  const [saving, setSaving] = useState(false);

  async function createUser(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetchAdmin("/api/admin/users", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`User ${data.name} created${data.booking_id ? " with booking" : ""}`);
        setForm({ name: "", email: "", role: "trekker", program_id: "" });
        setShowForm(false);
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create user");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  }

  async function toggleRole(userId, currentRole) {
    await fetchAdmin(`/api/admin/users/${userId}/role`, {
      method: "PUT",
      headers: jsonHeaders(),
      body: JSON.stringify({ role: currentRole === "admin" ? "trekker" : "admin" }),
    });
    onRefresh();
  }

  return (
    <div data-testid="users-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">Users ({users.length})</h2>
        <Button
          onClick={() => { setShowForm(!showForm); setForm({ name: "", email: "", role: "trekker", program_id: "" }); }}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-1.5" />{showForm ? "Cancel" : "Add User"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={createUser} className="bg-stone-900/60 border border-stone-800 rounded-xl p-6 mb-6 space-y-4" data-testid="user-create-form">
          <h3 className="text-white font-semibold">Create New User</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-300">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" data-testid="user-name-input" placeholder="Full name" />
            </div>
            <div>
              <Label className="text-stone-300">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" data-testid="user-email-input" placeholder="user@email.com" />
            </div>
            <div>
              <Label className="text-stone-300">Role</Label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="mt-1 w-full bg-stone-800 border border-stone-700 text-white rounded-xl h-10 px-3" data-testid="user-role-select">
                <option value="trekker">Trekker</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <Label className="text-stone-300">Assign Trek (optional)</Label>
              <select value={form.program_id} onChange={e => setForm(p => ({ ...p, program_id: e.target.value }))} className="mt-1 w-full bg-stone-800 border border-stone-700 text-white rounded-xl h-10 px-3" data-testid="user-program-select">
                <option value="">No trek assigned</option>
                {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-user-btn">
            <Save className="w-4 h-4 mr-1.5" />{saving ? "Creating..." : "Create User"}
          </Button>
        </form>
      )}

      <div className="space-y-3">
        {users.map(u => (
          <div key={u.user_id} className="bg-stone-900/60 border border-stone-800 rounded-xl p-4 flex items-center justify-between" data-testid={`user-${u.user_id}`}>
            <div className="flex items-center gap-3">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
                  <span className="text-amber-400 font-semibold">{u.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
              <div>
                <p className="text-white font-medium">{u.name}</p>
                <p className="text-stone-400 text-sm">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={u.role === "admin" ? "bg-amber-500/20 text-amber-400" : "bg-stone-700 text-stone-300"}>{u.role}</Badge>
              <Button onClick={() => toggleRole(u.user_id, u.role)} variant="outline" size="sm" className="border-stone-700 text-stone-300 rounded-lg text-xs">
                {u.role === "admin" ? "Remove Admin" : "Make Admin"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
